import React, { useState } from 'react';
import {
  VStack,
  Box,
  Text,
  Flex,
  HStack,
  Icon,
  Link,
  Input,
  Button,
} from '@chakra-ui/react';
import { FiLink } from 'react-icons/fi';
import { SocialChannel } from '@foodfluence/shared';
import { getChannelDisplayName, getChannelIcon } from '../../../utils/channelUtils';
import { ff } from '../../../theme';
import { FeedbackBox, StatusPill, PillTone, linkInputProps } from './submissionSheetParts';

interface ChannelLink {
  channel: SocialChannel;
  link: string;
  status?: 'approved' | 'pendingVerification' | 'pendingSubmission';
  submittedLink?: string;
  rejectionReason?: string;
  /** The brand withdrew this channel's slot: shown greyed, with no link to give. */
  voided?: boolean;
}

interface ContentLinkSubmissionProps {
  channelLinks: ChannelLink[];
  submittingChannel: SocialChannel | null;
  onChannelLinkChange: (channel: string, value: string) => void;
  onSubmitLink: (channel: SocialChannel, link: string) => void;
}

export const ContentLinkSubmission: React.FC<ContentLinkSubmissionProps> = ({
  channelLinks,
  submittingChannel,
  onChannelLinkChange,
  onSubmitLink
}) => {
  if (channelLinks.length === 0) return null;

  return (
    <VStack spacing="16px" align="stretch">
      <Box>
        <Text as="h2" m={0} textStyle="display" fontSize="27px" lineHeight={1.12} color={ff.ink} mb="8px">
          Submit post links
        </Text>
        <Text color={ff.body} fontSize="15px" lineHeight={1.45}>
          Now that your content is approved, please submit the links to your live posts for each platform.
        </Text>
      </Box>

      {channelLinks.map(({ channel, link, submittedLink, status, rejectionReason, voided }) => {
        const ChannelIcon = getChannelIcon(channel);
        const isSubmitted = Boolean(submittedLink);
        const isPendingVerification = status === 'pendingVerification';
        const isPendingSubmission = status === 'pendingSubmission';
        const isApproved = status === 'approved';
        const isRejected = Boolean(rejectionReason);
        const pillTone: PillTone = isApproved ? 'green' : isRejected ? 'red' : isPendingVerification ? 'dark' : 'grey';

        if (voided) {
          return (
            <Box key={channel} opacity={0.55} p="14px" borderRadius="18px" border="1px solid" borderColor={ff.line}>
              <Flex align="center" gap="10px" minH="28px">
                <Icon as={ChannelIcon} boxSize="20px" color={ff.muted} />
                <Text flex={1} minW={0} fontSize="15px" fontWeight={600} color={ff.text2}>{getChannelDisplayName(channel)} link</Text>
                <StatusPill tone="grey">Removed by the brand</StatusPill>
              </Flex>
              <Text mt="6px" fontSize="14px" color={ff.text2}>
                You are no longer expected to post on {getChannelDisplayName(channel)} for this piece.
              </Text>
            </Box>
          );
        }

        return (
          <Box key={channel} p="14px" borderRadius="18px" border="1px solid" borderColor={ff.border}>
            <Flex align="center" gap="10px" minH="28px" mb="12px">
              <Icon as={ChannelIcon} boxSize="20px" color={ff.ink} />
              <Text flex={1} minW={0} fontSize="15px" fontWeight={600} color={ff.ink}>{getChannelDisplayName(channel)} link</Text>
              {isSubmitted && (
                <StatusPill tone={pillTone}>
                  {
                    isApproved ? 'Approved' :
                    isRejected ? 'Needs update' :
                    isPendingVerification ? 'In review' :
                    'Pending submission'
                  }
                </StatusPill>
              )}
            </Flex>
            <VStack spacing="10px" align="stretch">
              {isSubmitted && (
                <Flex px="12px" py="10px" bg={ff.grey} borderRadius="14px" justify="space-between" align="center" gap="12px">
                  <Text fontSize="13px" color={ff.text2} flexShrink={0} whiteSpace="nowrap">
                    Submitted link
                  </Text>
                  <Link
                    href={submittedLink}
                    isExternal
                    color={ff.ink}
                    fontSize="14px"
                    fontWeight={600}
                    textDecoration="underline"
                    display="flex"
                    alignItems="center"
                    minW={0}
                    overflow="hidden"
                  >
                    <Text as="span" isTruncated minW={0}>
                      {submittedLink}
                    </Text>
                    <Icon as={FiLink} ml={2} boxSize="14px" flexShrink={0} />
                  </Link>
                </Flex>
              )}
              {(!isSubmitted || isPendingSubmission || isRejected) && (
                <VStack spacing="10px" align="stretch">
                  {isRejected && rejectionReason && (
                    <FeedbackBox lead="Feedback from our team:">{rejectionReason}</FeedbackBox>
                  )}
                  <HStack spacing="8px">
                    <Input
                      placeholder="Enter your post link"
                      value={link}
                      onChange={(e) => onChannelLinkChange(channel, e.target.value)}
                      {...linkInputProps(false)}
                    />
                    <Button
                      variant={isSubmitted ? 'inkOutline' : 'ink'}
                      h="48px"
                      px="20px"
                      fontSize="15px"
                      flex="none"
                      isLoading={submittingChannel === channel}
                      onClick={() => onSubmitLink(channel, link)}
                      disabled={!link.trim() || submittingChannel === channel}
                    >
                      {isSubmitted ? (isRejected ? 'Submit' : 'Update') : 'Submit'}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
};
